import { useId, useMemo } from 'react';
import { calculateGuideBounds } from '../../salon/utils/faceGuidanceLogic';
import './FaceIdentityScanOverlay.css';

const STATUS = {
  green: {
    stroke: '#e8c98a',
    soft: 'rgba(232, 201, 138, 0.5)',
    label: '#f0d9a8',
  },
  yellow: {
    stroke: '#d4af73',
    soft: 'rgba(212, 175, 115, 0.48)',
    label: '#f0d9a8',
  },
  red: {
    stroke: '#c9a06a',
    soft: 'rgba(201, 160, 106, 0.4)',
    label: '#e8d4b0',
  },
  orange: {
    stroke: '#d4af73',
    soft: 'rgba(212, 175, 115, 0.45)',
    label: '#f0d9a8',
  },
};

/**
 * Luxury gold HUD overlay for Naturals face-identity capture.
 */
const FaceIdentityScanOverlay = ({
  statusColor = 'red',
  message = 'Position your face',
  videoWidth = 640,
  videoHeight = 480,
  showOverlay = true,
  countdown = null,
  lightingOk = false,
  faceDetected = false,
  faceStraight = false,
  scanning = false,
  engineReady = true,
}) => {
  const uid = useId().replace(/:/g, '');
  const guide = useMemo(() => {
    const bounds = calculateGuideBounds(videoWidth, videoHeight);
    return {
      cx: bounds.centerX,
      cy: bounds.centerY,
      rx: bounds.width / 2,
      ry: bounds.height / 2,
    };
  }, [videoWidth, videoHeight]);

  /* Percent of viewBox so spin stays on the oval when the SVG is scaled to the video. */
  const spinOrigin = useMemo(
    () => ({
      ['--fi-spin-ox']: `${(guide.cx / Math.max(videoWidth, 1)) * 100}%`,
      ['--fi-spin-oy']: `${(guide.cy / Math.max(videoHeight, 1)) * 100}%`,
      transformOrigin: `${(guide.cx / Math.max(videoWidth, 1)) * 100}% ${(guide.cy / Math.max(videoHeight, 1)) * 100}%`,
    }),
    [guide.cx, guide.cy, videoWidth, videoHeight],
  );

  if (!showOverlay) return null;

  const colors = STATUS[statusColor] || STATUS.red;
  const locked = statusColor === 'green' && faceDetected && faceStraight && lightingOk;
  const isCounting = countdown !== null && countdown > 0;
  const modeClass = [
    'fi-scan',
    locked ? 'fi-scan--locked' : '',
    scanning ? 'fi-scan--identifying' : '',
    !engineReady ? 'fi-scan--warming' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const clipId = `fi-face-clip-${uid}`;
  const glowId = `fi-face-glow-${uid}`;
  const beamId = `fi-scan-beam-${uid}`;

  const kicker = !engineReady
    ? 'Preparing'
    : scanning
      ? 'Recognizing'
      : locked
        ? 'Ready'
        : faceDetected
          ? 'Aligning'
          : 'Scan';

  const displayMessage = !engineReady
    ? 'Preparing face check-in…'
    : scanning
      ? 'Recognizing you…'
      : message;

  return (
    <div className={modeClass} aria-hidden="true">
      <svg
        className="fi-scan__svg"
        viewBox={`0 0 ${videoWidth} ${videoHeight}`}
        preserveAspectRatio="xMidYMid slice"
        width="100%"
        height="100%"
      >
        <defs>
          <clipPath id={clipId}>
            <ellipse cx={guide.cx} cy={guide.cy} rx={guide.rx} ry={guide.ry} />
          </clipPath>
          <radialGradient id={glowId} cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.2" />
            <stop offset="60%" stopColor={colors.stroke} stopOpacity="0.05" />
            <stop offset="100%" stopColor={colors.stroke} stopOpacity="0" />
          </radialGradient>
          <linearGradient id={beamId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.stroke} stopOpacity="0" />
            <stop offset="42%" stopColor={colors.stroke} stopOpacity="0.16" />
            <stop offset="50%" stopColor="#fff8e8" stopOpacity="0.92" />
            <stop offset="58%" stopColor={colors.stroke} stopOpacity="0.16" />
            <stop offset="100%" stopColor={colors.stroke} stopOpacity="0" />
          </linearGradient>
        </defs>

        <path
          className="fi-scan__vignette"
          fillRule="evenodd"
          d={`M0 0 H${videoWidth} V${videoHeight} H0 Z
              M${guide.cx} ${guide.cy}
              m ${-guide.rx} 0
              a ${guide.rx} ${guide.ry} 0 1 0 ${guide.rx * 2} 0
              a ${guide.rx} ${guide.ry} 0 1 0 ${-guide.rx * 2} 0`}
        />

        <ellipse
          className="fi-scan__aura"
          cx={guide.cx}
          cy={guide.cy}
          rx={guide.rx * 1.14}
          ry={guide.ry * 1.14}
          fill={`url(#${glowId})`}
        />

        <g clipPath={`url(#${clipId})`} className="fi-scan__reticle">
          {[-0.5, -0.25, 0, 0.25, 0.5].map((t) => (
            <line
              key={`v-${t}`}
              x1={guide.cx + guide.rx * t}
              y1={guide.cy - guide.ry}
              x2={guide.cx + guide.rx * t}
              y2={guide.cy + guide.ry}
            />
          ))}
          {[-0.55, -0.3, 0, 0.3, 0.55].map((t) => (
            <line
              key={`h-${t}`}
              x1={guide.cx - guide.rx}
              y1={guide.cy + guide.ry * t}
              x2={guide.cx + guide.rx}
              y2={guide.cy + guide.ry * t}
            />
          ))}
          <rect
            className="fi-scan__beam"
            x={guide.cx - guide.rx}
            y={guide.cy - guide.ry}
            width={guide.rx * 2}
            height={Math.max(18, guide.ry * 0.2)}
            fill={`url(#${beamId})`}
          />
        </g>

        <ellipse
          className="fi-scan__ring fi-scan__ring--base"
          cx={guide.cx}
          cy={guide.cy}
          rx={guide.rx}
          ry={guide.ry}
          stroke={colors.stroke}
        />
        <g className="fi-scan__spin fi-scan__spin--fast" style={spinOrigin}>
          <ellipse
            className="fi-scan__ring fi-scan__ring--dash"
            cx={guide.cx}
            cy={guide.cy}
            rx={guide.rx + 10}
            ry={guide.ry + 10}
            stroke={colors.stroke}
          />
        </g>
        <g className="fi-scan__spin fi-scan__spin--slow" style={spinOrigin}>
          <ellipse
            className="fi-scan__ring fi-scan__ring--orbit"
            cx={guide.cx}
            cy={guide.cy}
            rx={guide.rx + 22}
            ry={guide.ry + 22}
            stroke={colors.soft}
          />
        </g>

        {[
          [-1, -1],
          [1, -1],
          [-1, 1],
          [1, 1],
        ].map(([sx, sy], i) => {
          const bx = guide.cx + sx * (guide.rx - 8);
          const by = guide.cy + sy * (guide.ry - 8);
          const len = Math.min(guide.rx, guide.ry) * 0.18;
          return (
            <path
              key={`br-${i}`}
              className="fi-scan__bracket"
              d={`M ${bx} ${by + sy * len} L ${bx} ${by} L ${bx + sx * len} ${by}`}
              stroke={colors.stroke}
            />
          );
        })}

        {[-1, 1].map((side) => (
          <line
            key={`tick-h-${side}`}
            className="fi-scan__tick"
            x1={guide.cx + side * (guide.rx + 4)}
            y1={guide.cy - 14}
            x2={guide.cx + side * (guide.rx + 4)}
            y2={guide.cy + 14}
            stroke={colors.stroke}
          />
        ))}
        {[-1, 1].map((side) => (
          <line
            key={`tick-v-${side}`}
            className="fi-scan__tick"
            x1={guide.cx - 14}
            y1={guide.cy + side * (guide.ry + 4)}
            x2={guide.cx + 14}
            y2={guide.cy + side * (guide.ry + 4)}
            stroke={colors.stroke}
          />
        ))}
      </svg>

      <div className="fi-scan__hud">
        <div className="fi-scan__message" style={{ color: colors.label }}>
          <span className="fi-scan__message-kicker">{kicker}</span>
          <span className="fi-scan__message-text">{displayMessage}</span>
          <div className="fi-scan__status-row">
            <span className={faceDetected ? 'is-on' : ''}>Face</span>
            <span className={faceStraight ? 'is-on' : ''}>Align</span>
            <span className={lightingOk ? 'is-on' : ''}>Light</span>
            <span className={engineReady ? 'is-on' : ''}>Ready</span>
          </div>
        </div>
      </div>

      {isCounting && (
        <div
          className="fi-scan__countdown"
          style={{
            left: `${(guide.cx / videoWidth) * 100}%`,
            top: `${(guide.cy / videoHeight) * 100}%`,
          }}
        >
          <span>{countdown}</span>
        </div>
      )}
    </div>
  );
};

export default FaceIdentityScanOverlay;
